import { Image, Tag, Typography } from 'antd';
import type { PosVariantSearchItem } from '../types';

interface Props {
  item: PosVariantSearchItem;
  onClick: () => void;
}

type TagColor = 'blue' | 'red' | 'green' | 'orange' | 'purple' | 'default';

function trackingTag(mode: string | undefined): { color: TagColor; label: string } {
  if (mode === 'serial') return { color: 'blue', label: 'Serial' };
  if (mode === 'quantity') return { color: 'red', label: 'Số lượng' };
  return { color: 'default', label: '-' };
}

function pricingTag(model: string | null | undefined): { color: TagColor; label: string } {
  if (model === 'FIXED') return { color: 'green', label: 'Giá: cố định' };
  if (model === 'DYNAMIC_MARKET') return { color: 'orange', label: 'Giá: theo thị trường' };
  if (model === 'DYNAMIC_SERIAL_WEIGHT') return { color: 'purple', label: 'Giá: theo Serial' };
  return { color: 'default', label: 'Giá: -' };
}

export function QRSuggestItem({ item, onClick }: Props) {
  const qrCode = item.serial
    ? `${item.variant.variant_code}_${item.serial}`
    : item.variant.variant_code;
  const weightG = item.variant.attrs.weight_g?.attribute_value ?? '-';
  const isSerial = !!item.serial;

  const channels = [
    item.variant.is_sellable_pos && 'POS',
    item.variant.is_sellable_web && 'WEB',
    item.variant.is_sellable_app && 'APP',
  ].filter(Boolean) as string[];

  const t = trackingTag(item.product.tracking_mode);
  const p = pricingTag(item.variant.pricing_model);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onMouseDown={(e) => e.preventDefault()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      style={{
        padding: 12,
        cursor: 'pointer',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        gap: 12,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = '#fafafa';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = '';
      }}
    >
      <div style={{ flexShrink: 0 }}>
        <Image
          src={item.product.image || ''}
          alt={item.product.name}
          width={60}
          height={60}
          preview={false}
          style={{ objectFit: 'cover', borderRadius: 4 }}
          fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
        />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Typography.Text strong>{item.product.name}</Typography.Text>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4, marginBottom: 4 }}>
          <Tag color={t.color}>{t.label}</Tag>
          <Tag color={p.color}>{p.label}</Tag>
          <Tag color={item.variant.is_sellable ? 'green' : 'default'}>
            Kênh: {channels.length > 0 ? channels.join(', ') : '-'}
          </Tag>
          <Tag color={item.variant.is_published != null ? 'green' : 'default'}>
            {item.variant.is_published != null ? 'Public' : '-'}
          </Tag>
        </div>
        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
          QR Code: {qrCode}
        </Typography.Text>
        {isSerial ? (
          <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
            Serial: {item.serial}
          </Typography.Text>
        ) : (
          item.stock_quantity !== null && (
            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
              Số lượng tồn: {item.stock_quantity}
            </Typography.Text>
          )
        )}
        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
          Khối lượng: ~{weightG}g
        </Typography.Text>
      </div>
    </div>
  );
}
